/**
 * i18n initialization — react-i18next + http backend (custom fetcher that
 * hits our FastAPI /api/i18n/{lang}/{ns}.json endpoint).
 *
 * Default locale is es-AR. Catalogs are bundled in full for every supported
 * namespace so the UI never paints raw `key.like.this` strings while waiting
 * on the lazy fetch. The API fetch still runs after mount and overwrites the
 * bundled defaults — useful if a future deploy adds keys without rebuilding.
 */
import i18n from "i18next";
import { initReactI18next } from "react-i18next";
import esAuth from "./bundled/es-AR.auth.json";
import esCommon from "./bundled/es-AR.common.json";
import esWorkspace from "./bundled/es-AR.workspace.json";
import esVoice from "./bundled/es-AR.voice.json";
import esAccount from "./bundled/es-AR.account.json";
import esErrors from "./bundled/es-AR.errors.json";
import enAuth from "./bundled/en.auth.json";
import enCommon from "./bundled/en.common.json";
import enWorkspace from "./bundled/en.workspace.json";
import enVoice from "./bundled/en.voice.json";
import enAccount from "./bundled/en.account.json";
import enErrors from "./bundled/en.errors.json";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const BUNDLED = {
  "es-AR": {
    common: esCommon,
    auth: esAuth,
    workspace: esWorkspace,
    voice: esVoice,
    account: esAccount,
    errors: esErrors,
  },
  en: {
    common: enCommon,
    auth: enAuth,
    workspace: enWorkspace,
    voice: enVoice,
    account: enAccount,
    errors: enErrors,
  },
};

i18n.use(initReactI18next).init({
  resources: BUNDLED,
  lng: "es-AR",
  fallbackLng: "es-AR",
  defaultNS: "common",
  ns: ["common", "auth", "account", "errors", "workspace", "voice"],
  // Our catalogs use flat dotted keys (e.g. "login.title"), so we disable
  // keySeparator traversal — `t("login.title")` looks up the literal key
  // instead of navigating bundle.login.title.
  keySeparator: false,
  nsSeparator: ":",
  interpolation: { escapeValue: false },
  react: {
    useSuspense: false,
    // Re-render on namespace additions (the API refresh below).
    bindI18n: "languageChanged loaded added",
  },
});

// Lazy-fetch the same namespaces from the API and merge them in. This lets
// admins update translations server-side without a frontend redeploy.
const _loaded = new Set();
export async function loadNamespace(lng, ns) {
  const key = `${lng}/${ns}`;
  if (_loaded.has(key)) return;
  try {
    const res = await fetch(`${API}/i18n/${lng}/${ns}.json`);
    if (res.ok) {
      const data = await res.json();
      i18n.addResourceBundle(lng, ns, data, true, true);
      _loaded.add(key);
    }
  } catch { /* keep bundled defaults */ }
}

// Switch language; updates document.documentElement.lang and re-renders.
export async function setLocale(lng) {
  await Promise.all([
    loadNamespace(lng, "common"),
    loadNamespace(lng, "auth"),
    loadNamespace(lng, "account"),
    loadNamespace(lng, "errors"),
    loadNamespace(lng, "workspace"),
    loadNamespace(lng, "voice"),
  ]);
  await i18n.changeLanguage(lng);
  document.documentElement.lang = lng;
  try { localStorage.setItem("faro_locale", lng); } catch {}
  // Best-effort: persist on server if user is logged in.
  try {
    const token = localStorage.getItem("nexa_token") || localStorage.getItem("faro_token");
    if (token) {
      await fetch(`${API}/me/preferences`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json", "Authorization": `Bearer ${token}` },
        body: JSON.stringify({ locale: lng }),
      });
    }
  } catch {}
  window.dispatchEvent(new Event("locale:change"));
}

// Initial resolution: stored locale > /api/i18n/me > es-AR.
export async function initLocale() {
  let lng = null;
  try { lng = localStorage.getItem("faro_locale"); } catch {}
  if (!lng || !["es-AR", "en"].includes(lng)) {
    try {
      const res = await fetch(`${API}/i18n/me`);
      if (res.ok) {
        const d = await res.json();
        if (d.locale) lng = d.locale;
      }
    } catch {}
  }
  if (!lng) lng = "es-AR";
  await setLocale(lng);
}

export default i18n;
